import type { Json } from "./types";
import type { ActiveSupportMode } from "./support-mode-ref";
import { SavedBatchPreDispatchError, type CoherentSavedBatchTransport, type CoherentSavedBatchListTransport } from "@/components/contributions/saved-batch/coherent-port";
import type { SavedBatchReadRequest } from "@/components/contributions/saved-batch/core-port";
import type {
  ChangeContributionBatchDateRequest, ContributionBatchCompareAndSwap,
  appendContributionBatchArgs, updateContributionBatchDetailsArgs, updateContributionBatchMemberArgs,
} from "@/components/contributions/saved-batch/coherent-contract";

export interface SavedBatchAuthenticatedContext {
  actorId: string;
  organizationId: string;
  support: ActiveSupportMode | null;
}

type Session = { user: { id: string }; access_token: string; expires_at?: number };
export interface SavedBatchRpcDependencies {
  url: string;
  publishableKey: string;
  schema: string;
  ensureFreshSession(): Promise<boolean>;
  getSession(): PromiseLike<{ data: { session: Session | null }; error: unknown }>;
  getSupportMode(): ActiveSupportMode | null;
  /** The application's support-aware fetch, including its audit side effect. */
  fetch: typeof fetch;
  now?: () => number;
}

const RPC_NAMES = [
  "finance_read_contribution_batch", "finance_list_contribution_batches",
  "finance_change_contribution_batch_date", "finance_reopen_contribution_batch",
  "finance_append_contribution_batch", "finance_update_contribution_batch_details",
  "finance_update_contribution_batch_member",
] as const;
type RpcName = typeof RPC_NAMES[number];
type RpcArgs = SavedBatchReadRequest | { p_organization_id: string }
  | ChangeContributionBatchDateRequest | ContributionBatchCompareAndSwap
  | ReturnType<typeof appendContributionBatchArgs>
  | ReturnType<typeof updateContributionBatchDetailsArgs>
  | ReturnType<typeof updateContributionBatchMemberArgs>;
type RpcResult = { data: Json | null; error: { message: string; code?: string } | null };
export type AuthenticatedSavedBatchTransport = CoherentSavedBatchTransport & CoherentSavedBatchListTransport;

/** Bump deliberately when reviewed adapter/DTO/recovery semantics become
 * incompatible. Stable across builds and ordinary token renewal. */
export const SAVED_BATCH_ADAPTER_CONTRACT_VERSION = "coherent-contribution-batch-r3-v2";

function isJson(value: unknown): value is Json {
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isJson);
  return typeof value === "object" && Object.values(value).every(isJson);
}

class RequestBoundaryError extends Error {
  constructor(message: string, readonly code: string) { super(message); }
}

/** Narrow REST binding to the existing coherent contracts. Server authorization,
 * accounting guards and DTO validation remain mandatory; local session checks
 * prevent stale UI requests and do not establish database authority. */
export function createSavedBatchRpcTransport(
  expected: SavedBatchAuthenticatedContext,
  isCurrent: () => boolean,
  dependencies: SavedBatchRpcDependencies,
): AuthenticatedSavedBatchTransport {
  const context = { ...expected, support: expected.support ? { ...expected.support } : null };
  const now = dependencies.now ?? Date.now;
  const checkContext = () => {
    const support = dependencies.getSupportMode();
    if (!isCurrent() || !context.actorId || !context.organizationId
      || (context.support === null ? support !== null : !support
        || support.sessionId !== context.support.sessionId
        || support.targetOrgId !== context.support.targetOrgId
        || support.adminUserId !== context.support.adminUserId
        || context.support.targetOrgId !== context.organizationId
        || context.support.adminUserId !== context.actorId)) {
      throw new RequestBoundaryError("Saved batch access changed. Reload before continuing.", "SAVED_BATCH_SCOPE_CHANGED");
    }
  };
  const checkSession = (session: Session | null, error: unknown) => {
    checkContext();
    if (error || !session || session.user.id !== context.actorId || !session.access_token
      || !Number.isFinite(session.expires_at) || session.expires_at <= now() / 1000) {
      throw new RequestBoundaryError("Sign in again before accessing this saved batch.", "SAVED_BATCH_SESSION_REQUIRED");
    }
    return session;
  };

  return {
    async rpc(name: RpcName, args: RpcArgs): Promise<RpcResult> {
      let dispatched = false;
      try {
        checkContext();
        if (!RPC_NAMES.includes(name) || args?.p_organization_id !== context.organizationId) {
          throw new RequestBoundaryError("Invalid saved batch request.", "SAVED_BATCH_INVALID_REQUEST");
        }
        if (!dependencies.url || !dependencies.publishableKey || !dependencies.schema) {
          throw new RequestBoundaryError("Saved batch connection is unavailable.", "SAVED_BATCH_CONNECTION_REQUIRED");
        }
        // Capture the exact submitted arguments before any asynchronous preflight.
        const body = JSON.stringify(args);
        const fresh = await dependencies.ensureFreshSession();
        checkContext();
        if (fresh !== true) throw new RequestBoundaryError("Sign in again before accessing this saved batch.", "SAVED_BATCH_SESSION_REQUIRED");
        const sessionResult = await dependencies.getSession();
        const session = checkSession(sessionResult.data.session, sessionResult.error);
        // Once fetch is invoked, even a synchronous throw is conservatively
        // ambiguous. Only the checks above can prove this attempt was not sent.
        dispatched = true;
        const response = await dependencies.fetch(`${dependencies.url.replace(/\/$/, "")}/rest/v1/rpc/${name}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json", Accept: "application/json",
            "Accept-Profile": dependencies.schema, "Content-Profile": dependencies.schema,
            apikey: dependencies.publishableKey, Authorization: `Bearer ${session.access_token}`,
          },
          body,
        });
        checkSession(session, null);
        const value: unknown = await response.json();
        checkSession(session, null);
        // Re-read auth after dispatch as well: a late result must never be adopted
        // by another actor, even before the React auth context catches up.
        const finalSession = await dependencies.getSession();
        checkSession(finalSession.data.session, finalSession.error);
        if (response.ok !== true) {
          if (!value || typeof value !== "object" || Array.isArray(value)
            || !("message" in value) || typeof value.message !== "string" || !value.message
            || ("code" in value && typeof value.code !== "string")) {
            throw new RequestBoundaryError("Invalid saved batch error response.", "SAVED_BATCH_INVALID_RESPONSE");
          }
          const error: { message: string; code?: string } = { message: value.message };
          if ("code" in value && typeof value.code === "string") error.code = value.code;
          return { data: null, error };
        }
        if (!isJson(value)) throw new RequestBoundaryError("Invalid saved batch response.", "SAVED_BATCH_INVALID_RESPONSE");
        return { data: value, error: null };
      } catch (error) {
        // Neither raw exceptions nor request headers are exposed or logged.
        const failure = error instanceof RequestBoundaryError
          ? { message: error.message, code: error.code }
          : { message: "Saved batch request could not be completed. Check its status before trying again.", code: "SAVED_BATCH_REQUEST_FAILED" };
        return { data: null, error: dispatched ? failure : new SavedBatchPreDispatchError(failure.message, failure.code) };
      }
    },
  };
}
